<template>
  <div>
    <!-- dekstop -->
    <div class="page mapmode-bp-desktop">
      <MglMap
        class="map"
        :accessToken="ACCESS_TOKEN"
        :mapStyle="MAP_STYLE"
        :interactive="true"
        :minZoom="12.5"
        :center="HELSINKI_CENTER"
        :maxBounds="[
          [24.769913, 60.111244],
          [25.108299, 60.210934]
        ]"
        @load="onMapLoaded"
      >
        <MglMarker
          v-for="(location, i) in allLocations"
          :key="i"
          :coordinates="location.coordinates"
          color="#ffa09d"
          @click="onMarkerClickedDesktop(i)"
        >
          <BIconGeoAlt slot="marker" class="marker" :style="markerStyle(i)" />
          <MglPopup
            anchor="bottom"
            :offset="20"
            :closeButton="false"
            @close="onPopupRemoved(i)"
          >
            <gyra-store-card
              :location="location"
              @store-button-clicked="onStoreButtonClicked($event)"
            />
          </MglPopup>
        </MglMarker>
      </MglMap>
    </div>
    <!-- mobile -->
    <div class="page mapmode-bp-mobile">
      <MglMap
        class="map"
        :accessToken="ACCESS_TOKEN"
        :mapStyle="MAP_STYLE"
        :interactive="true"
        :minZoom="12.5"
        :center="HELSINKI_CENTER_MOBILE"
        :maxBounds="[
          [24.769913, 60.111244],
          [25.108299, 60.210934]
        ]"
        @load="onMapLoaded"
      >
        <MglMarker
          v-for="(location, i) in allLocations"
          :key="i"
          :coordinates="location.coordinates"
          color="#ffa09d"
          @click="onMarkerClickedMobile(i)"
        >
          <BIconGeoAlt slot="marker" class="marker" :style="markerStyle(i)" />
        </MglMarker>
      </MglMap>
      <swiper
        class="gyra-swiper-container"
        :options="storeSwiperOptions"
        ref="storeSwiper"
        @slideChangeTransitionEnd="onSlideChange($event)"
      >
        <swiper-slide
          v-for="(location, i) in allLocations"
          :id="`swiper-store-card-${i}`"
          class="gyra-swiper-slide"
          :key="i"
        >
          <gyra-store-card
            :location="location"
            @store-button-clicked="onStoreButtonClicked($event)"
          />
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script>
import Mapbox from "mapbox-gl";
import { MglMap, MglPopup, MglMarker } from "vue-mapbox";
import {
  ACCESS_TOKEN,
  MAP_STYLE,
  HELSINKI_CENTER_MOBILE_COORDINATES,
  HELSINKI_CENTER_COORDINATES,
  STORE_SWIPER_OPTIONS
} from "../constants";
import { getStores } from "../services/api";
import { BIconGeoAlt } from "bootstrap-vue";
import GyraStoreCard from "../components/atoms/gyra-store-card";

export default {
  name: "StoreMap",
  components: {
    GyraStoreCard,
    MglMap,
    MglPopup,
    MglMarker,
    BIconGeoAlt
  },
  data() {
    return {
      ACCESS_TOKEN: ACCESS_TOKEN,
      MAP_STYLE: MAP_STYLE,
      HELSINKI_CENTER: HELSINKI_CENTER_COORDINATES,
      HELSINKI_CENTER_MOBILE: HELSINKI_CENTER_MOBILE_COORDINATES,
      storeSwiperOptions: STORE_SWIPER_OPTIONS,

      stores: null,
      selectedIndex: 0
    };
  },
  created() {
    this.mapbox = Mapbox;
    this.map = null;
  },
  methods: {
    async onMapLoaded(event) {
      // const asyncActions = event.component.actions;
      //
      // await asyncActions.flyTo({
      //   center: HELSINKI_CENTER,
      //   zoom: 13.5,
      //   speed: 1
      // });

      this.map = event.map;
      const coords = this.stores[0].locations[0].coordinates;
      this.map.panTo([coords[0], coords[1] - 0.008]);
      const effectiveIndex = this.storeSwiper.slides.findIndex(
        slide =>
          +slide.attributes["data-swiper-slide-index"].value ===
          this.selectedIndex
      );
      this.storeSwiper.slideTo(effectiveIndex);
    },
    async getStores() {
      const response = await getStores();
      this.stores = response.data;
    },
    onStoreButtonClicked(store) {
      this.$router.push({
        name: "StorePage",
        params: {
          store: store
        }
      });
    },
    onMarkerClickedDesktop(index) {
      this.selectedIndex = index;
    },
    onMarkerClickedMobile(index) {
      this.selectedIndex = index;
      const effectiveIndex = this.storeSwiper.slides.findIndex(
        slide => +slide.attributes["data-swiper-slide-index"].value === index
      );
      this.storeSwiper.slideTo(effectiveIndex);
    },
    markerStyle(index) {
      return index === this.selectedIndex
        ? { color: "#ffa09d" }
        : { color: "#414042" };
    },
    onPopupRemoved(index) {
      if (this.selectedIndex === index) {
        this.selectedIndex = null;
      }
    },
    onSlideChange(swiper) {
      const el = swiper.slides[swiper.activeIndex];
      const index = el.attributes["data-swiper-slide-index"];
      this.selectedIndex = +index.value;
      const location = this.allLocations[+index.value];
      this.map.panTo([
        location.coordinates[0],
        location.coordinates[1] - 0.008
      ]);
    }
  },
  computed: {
    allLocations() {
      let locations = [];
      if (!this.stores) return locations;
      this.stores.forEach(store => {
        store.locations.forEach(location => {
          locations.push({
            ...location,
            ...{
              storeName: store.storeName,
              tags: store.tags
            }
          });
        });
      });
      return locations;
    },
    storeSwiper() {
      return this.$refs.storeSwiper.$swiper;
    }
  },
  mounted() {
    this.getStores();
  }
};
</script>

<style scoped lang="scss">
@import "~@/styles/global.scss";
.page {
  height: calc(100vh - #{$gyra-header-height-mobile});
  @media (min-width: $gyra-breakpoint-sm) {
    height: calc(100vh - #{$gyra-header-height});
    max-height: unset;
  }
}
.map {
  height: 100%;
}
.store-button {
  background-color: $gyra-salmon;
  border: none;
  max-width: 20rem;
}

.marker {
  font-size: 32px;
}

.mapmode-bp-mobile {
  display: flex;
  @media (min-width: 800px) {
    display: none;
  }
}
.mapmode-bp-desktop {
  display: none;
  @media (min-width: 800px) {
    display: flex;
  }
}
.gyra-swiper-container {
  width: 100%;
  height: 25%;
  min-height: 200px;
  position: absolute;
  bottom: 60px;

  z-index: 100;
}

.gyra-swiper-slide {
  border-radius: 10px !important;
  box-shadow: 10px 10px 30px 0 rgba(0, 0, 0, 0.16);
  background-color: #ffffff;
  padding: 0;
  height: 168px;
  /*width: 296px !important;*/
  width: 100%;
}
</style>

<style lang="scss">
.mapboxgl-popup {
  max-width: unset !important;
}
.mapboxgl-popup-content {
  border-radius: 10px !important;
  box-shadow: 10px 10px 30px 0 rgba(0, 0, 0, 0.16);
  background-color: #ffffff;
  padding: 0;
  height: 168px;
  width: 320px;
}
</style>
